import { type ActionCreator, on, type ReducerTypes } from '@ngrx/store';
import { type Draft, produce } from 'immer';

import { type EngineStateBase, runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementOverrides,
  type CanvasNamespaceId,
  type LayoutData,
  NS_HOST_TYPE_PREFIX,
  type SlotId,
} from '@blaze-runtime/engine-domain';
import {
  applyPropsPatch,
  isPageRootId,
  WrapperActionButton,
  WrapperField,
} from '@blaze-runtime/engine-domain';
import { logPageLayout, logPageLayoutWarn } from '@blaze-runtime/engine-util';
import {
  type CanvasElementPropertiesWithData,
  ELEMENT_DEFAULT_ACTIONS,
  type LabelIconAlignment,
  type PropertiesConfigInputContainerItemActionModel,
  type RenderElementDataRowContext,
} from '@blaze-shared/elements-domain';
import { deleteNodeByPath, getValueByPath, setValueByPath } from '@blaze-shared/workflow-domain';

import { isAILayout, isAILayoutEmpty } from '../../ai';
import { createElementDataSourceBinding } from '../../canvas-elements/element-model/data-binding';
import {
  arePropsWithContent,
  hasDataProps,
  hasFilterBuilderConfig,
  hasProps,
  hasSearchAndFilters,
  hasSlots,
  hasWrapperRefs,
  isElementRedirect,
  isWrapper,
} from '../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isFieldGenerationSuspended,
  isFormGroupLike,
  isFormList,
  isNavigationElement,
  isVariantOfFormContainer,
} from '../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { applyFormGroupDataSourceMutably } from '../../services/forms/editor/public-api';
import {
  copyPropsFromInactiveStyleSheetMutably,
  detachStyleSheetFromElementMutably,
  resetStylePropsMutably,
  setStyleSheetForActionMutably,
  setStyleSheetToElementMutably,
} from '../../services/styles';
import { findHostingOfRoot, isRoot } from '../../utils/canvas-element/path';
import {
  clearPageReferencesMutably,
  retrieveElementPageReferences,
} from '../../utils/elements/validation.utils';
import { updateElementHeight, updateElementWidth } from '../../utils/layout/element-resize';
import {
  insertColumnMutably,
  insertSlotMutably,
  moveSlotMutably,
} from '../../utils/layout/insert-column';
import { placeElement } from '../../utils/layout/place-element';
import { findParentByElementType } from '../../utils/layout/relationship';
import {
  deleteSlotMutably,
  removeColumnMutably,
  removeElementFromParentMutably,
  removeElementMutably,
} from '../../utils/layout/remove-element';
import {
  equalizeContainerColumnsWidth,
  setColumnWidthPercentDrag,
  setDesiredColumnSizingMode,
  setDesiredColumnWidthDirect,
} from '../../utils/layout/set-column-width';
import { setSlotAlignmentMutably } from '../../utils/layout/set-slot-alignment';
import {
  populateNamespaceForHostTypeMutably,
  updateNamespacesAfterMove,
} from '../../utils/layout/update-namespace';
import { elementsInternal } from '../elements-internal/elements-internal.actions';
import { entityAdapter } from '../engine/engine-adapters';
import { sanitizeConnectionsAndDataAfterUiChangeMutably } from '../runtime/runtime.reducer.utils';

import {
  addColumn,
  addExistingElementAtPath,
  addNewElementAtPath,
  addOverlayElement,
  addSlot,
  aiGenerationSettled,
  alertRemoveSuccess,
  appendFormAction,
  clearPageRefs,
  clearPageRefsFromElement,
  copyInactiveStyleSheetProps,
  currentLayoutsColumnIdsAmended,
  deleteColumn,
  deleteElement,
  deleteSlot,
  detachStyleSheet,
  elementDataSourceSelected,
  equalizeColumnsWidth,
  formColumnsRegenerated,
  formListRegenerated,
  formRegenerated,
  initializeAvatarFields,
  layoutInitialized,
  layoutReady,
  layoutsSaved,
  markLayoutsOffSync,
  moveSlot,
  removeElementForDrag,
  removeEmptyActionsFromElement,
  removeEmptyFiltersFromElement,
  removeOverlayElement,
  replaceElements,
  resetElementPropsSection,
  resetElementStyleProps,
  resetSelection,
  resetSlotPropsSection,
  revertElementPropsOverride,
  sanitizeAndUpdateWrappedElement,
  selectParent,
  selectPath,
  setApiCallAsDataSource,
  setColumnSizingMode,
  setColumnWidthDirect,
  setColumnWidthDrag,
  setElementDescription,
  setElementFilterBuilderColumns,
  setElementHeight,
  setElementProps as setElementPropsAction,
  setElementSearchColumns,
  setElementSlotsAlignment,
  setElementWidth,
  setNsForSlotHostType,
  setSlotAlignment,
  setSlotHorizontalAlignment,
  setSlotVerticalAlignment,
  setStyleSheet,
  setStyleSheetForAction,
  swapElementTypeSuccess,
  updateBinding,
  updateDropdownDynamicSearchSuccess,
  updateElementProps,
  updateElementPropsBatch,
  updateElementsActionPage,
  updateElementsPropsBatch,
  updateElementWrapper,
  updateFormGroupDataSource,
  updateSlotProps,
  wizardQuestionnaireRegenerated,
} from './elements.actions';
import {
  applyElementsDiffMutably,
  sanitizeWrapperRefsMutably,
  updateFiltersMutably,
} from './elements.reducer.utils';
import {
  ensureApiCallDataSourceBindingMutably,
  initializeAvatarFieldsMutably,
  updateDataBindingMutably,
  updateElementAfterValueBindingsUpdateMutably,
} from './reducer-utils/bindings.utils';
import {
  tryApplyLabelMutably,
  updateElementAfterSourceSelectionMutably,
  updateElementPropsFromAiWorkflowMutably,
} from './reducer-utils/element-update.utils';
import {
  revertPropsOverrideMutably,
  setElementPropsMutably,
  setPropsValueMutably,
  setSlotPropsMutably,
} from './reducer-utils/set-props.utils';

export const elementsReducerTypes: ReducerTypes<EngineStateBase, ActionCreator[]>[] = [
  on(layoutInitialized, (state, { layout }) =>
    produce(state, (draft) => {
      if (!layout.aiLayout) {
        return;
      }

      if (!isAILayout(layout.aiLayout)) {
        logPageLayoutWarn('ElementsReducer', 'layoutInitialized', (tag) => {
          console.warn(`${tag}: AI layout is not valid ${layout.pageId}`, {
            aiLayout: layout.aiLayout,
          });
        });
        return;
      }

      if (isAILayoutEmpty(layout.aiLayout)) {
        logPageLayoutWarn('ElementsReducer', 'layoutInitialized', (tag) => {
          console.warn(`${tag}: AI layout is empty ${layout.pageId}`, {
            aiLayout: layout.aiLayout,
          });
        });
        return;
      }

      logPageLayout('ElementsReducer', 'layoutInitialized', (tag) => {
        console.log(`${tag}: Marked layout to be processed by AI ${layout.pageId}`, {
          aiLayout: layout.aiLayout,
        });
      });

      draft.pendingPageCreations = {
        ...draft.pendingPageCreations,
        [layout.pageId]: { aiLayout: layout.aiLayout },
      };
    }),
  ),

  on(layoutReady, (state, { layout, elements }) => {
    return upsertLayout(state, layout, elements);
  }),

  on(layoutsSaved, (state, { layouts }) =>
    produce(state, (draft) => {
      layouts.forEach((layout) => {
        if (draft.layoutEntities?.[layout.pageId]) {
          draft.layoutEntities[layout.pageId] = layout;
        }
        if (draft.offSyncLayouts) {
          delete draft.offSyncLayouts[layout.pageId];
        }
      });
    }),
  ),
  on(markLayoutsOffSync, (state, { layoutsIds }) =>
    produce(state, (draft) => {
      const offSyncLayouts = (state.offSyncLayouts ? draft.offSyncLayouts : {}) as Record<
        CanvasNamespaceId,
        boolean
      >;
      for (const id of layoutsIds) {
        offSyncLayouts[id] = true;
      }
      draft.offSyncLayouts = offSyncLayouts;
    }),
  ),
  on(replaceElements, (state, { ns, elements }) => {
    const idsInNamespace = (state.ids as CanvasElementId[]).filter(
      (id) => state.entities[id]?.ns === ns,
    );
    const newState = entityAdapter.removeMany(idsInNamespace, state);
    return entityAdapter.addMany(elements, newState);
  }),
  on(selectPath, (state, { elementId, slotId, rowContext }) =>
    selectPathNode(state, elementId, slotId, rowContext),
  ),
  on(selectParent, (state, { elementId }) => selectParentNode(state, elementId)),
  on(resetSelection, (state) =>
    produce(state, (draft) => {
      draft.selectedPathNode = undefined;
    }),
  ),
  on(addNewElementAtPath, (state, { elementDefinition, atPath, initProps, style }) => {
    const result = placeElement(state, { elementDefinition, atPath, initProps, style });
    const newState = { ...state, ...result.state };
    return selectPathNode(newState, result.elementId);
  }),

  on(
    elementsInternal.placeNewElement,
    formListRegenerated,
    currentLayoutsColumnIdsAmended,
    formColumnsRegenerated,
    (state, { entityState }) =>
      produce(state, (draft) => {
        draft.entities = entityState.entities;
        draft.ids = entityState.ids;
      }),
  ),

  on(formRegenerated, (state, { elementIdsToRemove, elementsToUpsert }) =>
    produce(state, (draft) => {
      applyElementsDiffMutably(draft, { elementsToUpsert, elementIdsToRemove });
    }),
  ),

  on(wizardQuestionnaireRegenerated, (state, { elementIdsToRemove, elementsToUpsert }) =>
    produce(state, (draft) => {
      applyElementsDiffMutably(draft, { elementsToUpsert, elementIdsToRemove });
    }),
  ),

  on(aiGenerationSettled, (state, { elementIds }) =>
    produce(state, (draft) => {
      for (const elementId of elementIds) {
        const element = draft.entities[elementId];
        if (!isVariantOfFormContainer(element) || !isFieldGenerationSuspended(element)) {
          continue;
        }
        element.props.content.aiGeneratedFields = false;
      }
    }),
  ),

  on(swapElementTypeSuccess, (state, { elementId, entity }) =>
    produce(state, (draft) => {
      draft.entities[elementId] = entity;
    }),
  ),

  on(
    addExistingElementAtPath,
    (state, { elementId, atPath, skipSelect, removeFromCurrentParent }) => {
      if (removeFromCurrentParent) {
        state = produce(state, (draft) => {
          removeElementFromParentMutably(draft, elementId);
        });
      }
      const result = placeElement(state, { elementId, atPath });
      let newState = { ...state, ...result.state };
      if (!skipSelect) {
        newState = selectPathNode(newState, result.elementId);
      }
      newState = updateNamespacesAfterMove(newState, result.elementId);
      return produce(newState, (draft) => {
        sanitizeConnectionsAndDataAfterUiChangeMutably(draft);
      });
    },
  ),

  on(addColumn, (state, { elementId, slotId, columnPlacement, sizing }) =>
    produce(state, (draft) => {
      const result = insertColumnMutably(draft, elementId, slotId, columnPlacement, sizing);
      selectPathNodeMutably(draft, result.elementId, result.slotId);
    }),
  ),
  on(setSlotAlignment, (state, { elementId, slotId, alignment }) =>
    produce(state, (draft) => {
      setSlotAlignmentMutably(draft, elementId, slotId, alignment.horizontal, alignment.vertical);
    }),
  ),
  on(setSlotHorizontalAlignment, (state, { elementId, slotId, horizontalAlignment }) =>
    produce(state, (draft) => {
      setSlotAlignmentMutably(draft, elementId, slotId, horizontalAlignment, undefined);
    }),
  ),
  on(setSlotVerticalAlignment, (state, { elementId, slotId, verticalAlignment }) =>
    produce(state, (draft) => {
      setSlotAlignmentMutably(draft, elementId, slotId, undefined, verticalAlignment);
    }),
  ),
  on(setElementSlotsAlignment, (state, { elementId, alignment }) => {
    const element = state.entities[elementId];
    if (!hasSlots(element)) {
      return state;
    }

    return produce(state, (draft) => {
      element.slots.ids.forEach((slotId) => {
        setSlotAlignmentMutably(draft, elementId, slotId, alignment.horizontal, alignment.vertical);
      });
    });
  }),
  on(setColumnWidthDrag, (state, { elementId, slotId, width }) => {
    return setColumnWidthPercentDrag(state, elementId, slotId, width);
  }),
  on(setColumnWidthDirect, (state, { elementId, slotId, resizeData, width }) => {
    return setDesiredColumnWidthDirect(state, elementId, slotId, resizeData, width);
  }),
  on(equalizeColumnsWidth, (state, { elementId }) => {
    return equalizeContainerColumnsWidth(state, elementId);
  }),
  on(setColumnSizingMode, (state, { elementId, slotId, mode, resizeData }) => {
    return setDesiredColumnSizingMode(state, elementId, slotId, mode, resizeData);
  }),
  on(setElementWidth, (state, { elementId, width }) => {
    return updateElementWidth(state, elementId, width);
  }),
  on(setElementHeight, (state, { elementId, height, elementMeta }) => {
    return updateElementHeight(state, elementId, height, elementMeta);
  }),
  on(deleteElement, (state, { elementId }) =>
    produce(state, (draft) => {
      removeElementMutably(draft, elementId);
    }),
  ),
  on(deleteColumn, (state, { elementId, slotId }) =>
    produce(state, (draft) => {
      removeColumnMutably(draft, elementId, slotId);
    }),
  ),
  on(alertRemoveSuccess, (state, { elementId, path }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (hasProps(element)) {
        setValueByPath(element.props, path, undefined);
      }
    }),
  ),

  on(
    elementDataSourceSelected,
    (
      state,
      { elementId, isInitialSelection, dataSourceName, applyDynamicSearch, selectedDataSource },
    ) =>
      produce(state, (draft) => {
        updateElementAfterSourceSelectionMutably(
          draft,
          elementId,
          selectedDataSource,
          isInitialSelection,
          dataSourceName,
          applyDynamicSearch,
        );
      }),
  ),
  on(
    updateBinding,
    (
      state,
      {
        elementId,
        inputConfig,
        bindingKey,
        schema,
        isNewDataSource,
        filteredDatasetColumns,
        setNewColumnsVisible,
        setNewColumnsEditable,
        visibleFields,
      },
    ) =>
      produce(state, (draft) => {
        const element = draft.entities[elementId];
        if (!hasDataProps(element)) {
          throw new Error('Data properties missing');
        }

        const dataBinding = element.props.data.find((data) => data?.key === bindingKey);
        const dataSource = dataBinding?.dataSource;
        if (!dataBinding || !dataSource) {
          throw new Error(`Binding '${bindingKey}' missing`);
        }

        updateDataBindingMutably(
          element.props.data,
          inputConfig?.bindingDefs,
          bindingKey,
          schema,
          isNewDataSource,
        );
        if (
          bindingKey &&
          ['value', 'messages', 'conversations', 'conversationsInfo', 'optionsList'].includes(
            bindingKey,
          )
        ) {
          updateElementAfterValueBindingsUpdateMutably(
            element,
            isNewDataSource,
            filteredDatasetColumns,
            !!setNewColumnsVisible,
            !!setNewColumnsEditable,
            visibleFields,
          );
        }
        if (hasWrapperRefs(element)) {
          const elementColumns = dataBinding.dataSource?.columns;
          if (!elementColumns) {
            return;
          }

          for (const [refId, inputId] of Object.entries(getWrapperRefs(element))) {
            if (!WrapperField.isRefId(refId)) {
              continue;
            }

            const input = draft.entities[inputId];
            if (!hasDataProps(input)) {
              continue;
            }

            const inputDataSource = input.props.data.find(
              (b) => b.dataSource?.sourceType === 'formContainer',
            )?.dataSource;
            if (!inputDataSource) {
              continue;
            }

            const inputColumnId = inputDataSource.columns[0]?.id;
            const inputElementColumn = elementColumns.find((col) => col.id === inputColumnId);
            if (inputElementColumn) {
              inputDataSource.columns[0] = inputElementColumn;
            }
          }
        }
      }),
  ),
  on(updateElementsActionPage, (state, { pageId, patch }) =>
    produce(state, (draft) => {
      for (const id of draft.ids) {
        const element = draft.entities[id];
        if (!hasProps(element)) {
          continue;
        }

        for (const actionName of ELEMENT_DEFAULT_ACTIONS) {
          const actionTargetId = getValueByPath(element.props, `content.${actionName}.targetId`);
          if (actionTargetId === pageId) {
            setValueByPath(element.props, `content.${actionName}.targetType`, patch.pageType);
          }
        }
        const rowClickAction = getValueByPath(element.props, `content.rowClick.targetId`);
        if (rowClickAction) {
          for (const [key, val] of Object.entries(rowClickAction)) {
            if (isElementRedirect(val) && val.targetId === pageId) {
              setValueByPath(
                element.props,
                `content.rowClick.targetId.${key}.targetType`,
                patch.pageType,
              );
            }
          }
        }
      }
    }),
  ),
  on(updateElementsActionPage, (state, { pageId, patch }) =>
    produce(state, (draft) => {
      for (const id of draft.ids) {
        const element = draft.entities[id];
        if (!hasProps(element)) {
          continue;
        }

        for (const actionName of ELEMENT_DEFAULT_ACTIONS) {
          const actionTargetId = getValueByPath(element.props, `content.${actionName}.targetId`);
          if (actionTargetId === pageId) {
            setValueByPath(element.props, `content.${actionName}.targetType`, patch.pageType);
          }
        }
        const rowClickAction = getValueByPath(element.props, `content.rowClick.targetId`);
        if (rowClickAction) {
          for (const [key, val] of Object.entries(rowClickAction)) {
            if (isElementRedirect(val) && val.targetId === pageId) {
              setValueByPath(
                element.props,
                `content.rowClick.targetId.${key}.targetType`,
                patch.pageType,
              );
            }
          }
        }
      }
    }),
  ),
  on(sanitizeAndUpdateWrappedElement, (state, { elementId }) =>
    produce(state, (draft) => {
      sanitizeWrapperRefsMutably(draft, elementId);
    }),
  ),
  on(removeElementForDrag, (state, { elementId }) =>
    produce(state, (draft) => {
      removeElementFromParentMutably(draft, elementId);
    }),
  ),
  //#region properties-editor interactions
  on(updateElementProps, (state, { elementId, path, value, target }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId] as CanvasElement;
      setPropsValueMutably(element, path, value, target);
    }),
  ),

  on(
    revertElementPropsOverride,
    (state, { elementId, slotId, path, target, setOnElement, setOnStyle }) =>
      produce(state, (draft) => {
        const element = draft.entities[elementId] as CanvasElement;
        let elementOrSlot: { overrides?: CanvasElementOverrides } | undefined;

        if (slotId && hasSlots(element)) {
          elementOrSlot = element.slots[slotId];
        } else {
          elementOrSlot = element;
        }

        if (!elementOrSlot) {
          return;
        }

        revertPropsOverrideMutably(elementOrSlot, path, target, setOnElement, setOnStyle);
      }),
  ),

  on(resetElementStyleProps, (state, { elementId, slotId, elementDefinition }) =>
    produce(state, (draft) => {
      resetStylePropsMutably({
        entities: draft.entities,
        elementId,
        slotId,
        definition: elementDefinition,
      });
    }),
  ),

  on(updateElementPropsBatch, (state, { elementId, updates, target, exclusive }) =>
    produce(state, (draft) => {
      setElementPropsMutably(
        draft.entities[elementId] as CanvasElement,
        updates,
        target,
        exclusive,
      );
    }),
  ),
  on(
    updateElementsPropsBatch,
    (state, { updates: elementsUpdates, removeUndefinedFor, target, exclusive }) =>
      produce(state, (draft) => {
        elementsUpdates.forEach(({ elementId, updates }) => {
          const elementDraft = draft.entities[elementId] as CanvasElement;
          setElementPropsMutably(elementDraft, updates, target, exclusive, removeUndefinedFor);
        });
      }),
  ),
  on(setElementPropsAction, (state, { elementId, props }) => {
    const updatedElement: CanvasElementFrom<any> = {
      ...state.entities[elementId],
      props,
    };
    return {
      ...state,
      entities: {
        ...state.entities,
        [elementId]: updatedElement,
      },
    };
  }),

  on(appendFormAction, (state, { formId, buttonId, workflowId }) =>
    produce(state, (draft) => {
      const form = draft.entities[formId];
      if (!isElementType(form, 'formDetails') || !hasProps(form)) {
        return;
      }

      if (!form.props.content.formActions) {
        form.props.content.formActions = [];
      }
      if (!hasWrapperRefs(form)) {
        form.props.wrapper = { refs: {} };
      }
      if (!hasWrapperRefs(form)) {
        return;
      } //strict check

      const refs = Object.entries(form.props.wrapper.refs);
      // action already exists
      if (
        refs.some(([ref, elementId]) => elementId === buttonId && WrapperActionButton.isRefId(ref))
      ) {
        return;
      }

      const formActionId = crypto.randomUUID();
      const buttonElement = state.entities[buttonId];
      form.props.content.formActions.push({
        id: formActionId,
        label: (getValueByPath(buttonElement, 'props.content.button.label') as string) ?? 'Button',
        icon: (getValueByPath(buttonElement, 'props.content.button.icon') as string) ?? '',
        iconPosition:
          (getValueByPath(
            buttonElement,
            'props.content.button.iconPosition',
          ) as LabelIconAlignment) ?? 'left',
        showInlineLabel: true,
        action: { workflowId, action: 'custom' },
      });

      form.props.wrapper.refs[WrapperActionButton.getRefIds(formActionId)[0]] = buttonId;
    }),
  ),

  on(updateSlotProps, (state, { elementId, slotId, path, value, target, exclusive }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId] as CanvasElement;
      if (path.startsWith('$$')) {
        return;
      }

      setSlotPropsMutably(element, slotId, path, value, target, exclusive);
    }),
  ),
  on(resetElementPropsSection, (state, { elementId, path }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId] as CanvasElement;
      if (hasProps(element)) {
        deleteNodeByPath(element.props, path);
      }
    }),
  ),
  on(resetSlotPropsSection, (state, { elementId, slotId, path }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId] as CanvasElement;
      if (hasSlots(element)) {
        const slot = element.slots[slotId];
        if (slot) {
          deleteNodeByPath(slot.props, path);
        }
      }
    }),
  ),
  on(
    setApiCallAsDataSource,
    (state, { elementId, fields, wrapperDefinition, inputWorkflow, resultColumns }) =>
      produce(state, (draft) => {
        ensureApiCallDataSourceBindingMutably(draft, elementId, inputWorkflow.id, resultColumns);
        updateFiltersMutably(draft, elementId, fields, wrapperDefinition, inputWorkflow);
      }),
  ),
  on(updateElementWrapper, (state, { elementId, fields, wrapperDefinition, inputWorkflow }) =>
    produce(state, (draft) => {
      updateFiltersMutably(draft, elementId, fields, wrapperDefinition, inputWorkflow);
    }),
  ),

  on(removeEmptyActionsFromElement, (state, { elementId, pathToCheck }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (!hasProps(element)) {
        return;
      }
      const action = getValueByPath(element.props, pathToCheck) as
        | PropertiesConfigInputContainerItemActionModel
        | undefined;
      if (action?.label || action?.icon) {
        return;
      }

      const pathSegments = pathToCheck.split('.');
      const itemIndex = parseInt(pathSegments.pop() ?? '-1', 10);

      if (Number.isNaN(itemIndex)) {
        console.warn('Action Index to clear is incorrect. Make sure pathToCheck is set properly.');
        return;
      }

      if (itemIndex < 0) {
        return;
      }

      const collectionPath = pathSegments.join('.');
      const collection = getValueByPath(
        element.props,
        collectionPath,
      ) as PropertiesConfigInputContainerItemActionModel[];
      if (collection && Array.isArray(collection)) {
        collection.splice(itemIndex, 1);
      }
    }),
  ),

  on(removeEmptyFiltersFromElement, (state, { elementId }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (!hasSearchAndFilters(element)) {
        return;
      }

      const { searchAndFilters } = element.props.content;
      if (!searchAndFilters?.filters?.length) {
        return;
      }

      searchAndFilters.filters = searchAndFilters.filters.filter((filter) => !!filter.filterField);
    }),
  ),

  //#endregion
  on(setNsForSlotHostType, (state, { hostType, ns }) =>
    produce(state, (draft) => {
      const expectedSlotId = `${NS_HOST_TYPE_PREFIX}:${hostType}`;
      draft.ids.forEach((id) => {
        populateNamespaceForHostTypeMutably(expectedSlotId, ns, draft.entities[id]);
      });
    }),
  ),

  on(addOverlayElement, (state, { overlayElement }) => {
    let newState = entityAdapter.upsertOne(overlayElement, state);
    newState = produce(newState, (draft) => {
      draft.overlays = (draft.overlays || []).filter((id) => id !== overlayElement.id);
      draft.overlays.push(overlayElement.id);
      draft.selectedPathNode = undefined;
    });

    return newState;
  }),

  on(removeOverlayElement, (state, { hostedNs }) => {
    // Find overlays hosting provided namespace
    const overlayIdsToBeRemoved = (state.overlays || []).filter((overlayId) => {
      return state.entities[overlayId]?.slots?.main?.hosting?.ns === hostedNs;
    });
    let newState = entityAdapter.removeMany(overlayIdsToBeRemoved, state);
    newState = produce(newState, (draft) => {
      draft.overlays = (draft.overlays || []).filter((id) => !overlayIdsToBeRemoved.includes(id));
      draft.selectedPathNode = undefined;
    });

    return newState;
  }),

  on(clearPageRefs, (state, { pageId }) => {
    const newState = produce(state, (draft) => {
      draft.ids.forEach((id) => {
        clearPageReferencesMutably(pageId, draft.entities[id]);
      });
    });

    return newState;
  }),

  on(clearPageRefsFromElement, (state, { elementId }) => {
    return produce(state, (draft) => {
      const pageIds = retrieveElementPageReferences(draft.entities[elementId]).filter(
        (pageId) => !draft.pages.some((page) => page.id === pageId),
      );
      pageIds.forEach((pageId) => {
        clearPageReferencesMutably(pageId, draft.entities[elementId]);
      });
    });
  }),

  // Automations
  on(
    runtimeActions.applyAiGeneratedWorkflow,
    (state, { elementId, bindingKey, dataSourceName, workflow }) =>
      produce(state, (draft) => {
        const element = draft.entities[elementId];
        if (!hasProps(element)) {
          return;
        }

        const propsWithOptionalData: CanvasElementPropertiesWithData & object = element.props;

        // If data props missing - initialize them
        if (!propsWithOptionalData.data) {
          propsWithOptionalData.data = [];
        }

        // Type-check
        if (!hasDataProps(element)) {
          return;
        }

        const isInitialBinding = !element.props.data.some(({ key }) => key === bindingKey);
        if (isInitialBinding && dataSourceName) {
          tryApplyLabelMutably(draft, elementId, isInitialBinding, dataSourceName);
        }

        // Remove existing binding
        element.props.data = element.props.data.filter(({ key }) => key !== bindingKey);

        // Add a new binding
        element.props.data.push(
          createElementDataSourceBinding(bindingKey, workflow.id, dataSourceName),
        );

        // Update binding from AI Workflow
        updateElementPropsFromAiWorkflowMutably(element, workflow);
      }),
  ),

  on(
    updateFormGroupDataSource,
    (
      state,
      { elementId, slotId, dataSource, styleSheets, formContainerDefinition, buttonDefinition },
    ) =>
      produce(state, (draft) => {
        applyFormGroupDataSourceMutably(draft, {
          elementId,
          slotId,
          dataSource,
          styleSheets,
          formContainerDefinition,
          buttonDefinition,
        });
      }),
  ),

  on(setElementSearchColumns, (state, { elementId, useUserSelectedSearchColumns, columns }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];

      if (!element) {
        throw new Error('Element not found');
      }

      if (!hasSearchAndFilters(element)) {
        throw new Error('Element has no search and filters set');
      }

      const searchAndFilters = element.props.content.searchAndFilters;

      if (!useUserSelectedSearchColumns) {
        delete searchAndFilters.searchColumns;
        delete searchAndFilters.useUserSelectedSearchColumns;
      } else {
        // Make sure that stored columns are unique
        if (columns) {
          searchAndFilters.searchColumns = [...new Set(columns)];
        } else {
          delete searchAndFilters.searchColumns;
        }
        searchAndFilters.useUserSelectedSearchColumns = true;
      }
    }),
  ),

  on(
    setElementFilterBuilderColumns,
    (state, { elementId, useUserSelectedFilterBuilderColumns, columns }) =>
      produce(state, (draft) => {
        const element = draft.entities[elementId];

        if (!element) {
          throw new Error('Element not found');
        }

        if (!arePropsWithContent(element)) {
          throw new Error('Element has no content in props');
        } else if (element.props.content && !(element.props.content as any).filterBuilderConfig) {
          (element.props.content as any).filterBuilderConfig = {};
        }
        if (!hasFilterBuilderConfig(element)) {
          throw new Error('Element has no filterBuilderConfig in props');
        }

        const filterBuilderConfig = element.props.content.filterBuilderConfig;

        if (!useUserSelectedFilterBuilderColumns) {
          delete filterBuilderConfig.columns;
          delete filterBuilderConfig.useUserSelectedFilterBuilderColumns;
        } else {
          // Make sure that stored columns are unique
          if (columns) {
            filterBuilderConfig.columns = [...new Set(columns)];
          } else {
            delete filterBuilderConfig.columns;
          }
          filterBuilderConfig.useUserSelectedFilterBuilderColumns = true;
        }
      }),
  ),

  on(updateDropdownDynamicSearchSuccess, (state, { elementId, enable }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      // Currently only dropdown supports dynamic search
      if (!isElementType(element, 'dropdown')) {
        return;
      }

      element.props = applyPropsPatch(element.props, {
        content: { menu: { dynamicSearch: enable } },
      }) as typeof element.props;
    }),
  ),

  on(addSlot, (state, { elementId, index }) =>
    produce(state, (draft) => {
      insertSlotMutably(draft, elementId, index);
    }),
  ),

  on(moveSlot, (state, { elementId, oldIndex, newIndex }) =>
    produce(state, (draft) => {
      moveSlotMutably(draft, elementId, oldIndex, newIndex);
    }),
  ),

  on(deleteSlot, (state, { elementId, index }) =>
    produce(state, (draft) => {
      deleteSlotMutably(draft, elementId, index);
      // deleteSlotMutably removes the slot's child elements (e.g. a wizard step's form),
      // which can leave wrapper.refs still pointing at the removed element.
      sanitizeWrapperRefsMutably(draft, elementId);
    }),
  ),

  on(setStyleSheet, (state, { elementId, slotId, styleSheet, elementDefinition }) =>
    produce(state, (draft) => {
      setStyleSheetToElementMutably({
        entities: draft.entities,
        elementId,
        slotId,
        elementDefinition,
        styleSheet,
      });
    }),
  ),

  on(detachStyleSheet, (state, { elementId, slotId, styleProps, styleOverrides }) =>
    produce(state, (draft) => {
      detachStyleSheetFromElementMutably({
        entities: draft.entities,
        elementId,
        slotId,
        styleProps,
        styleOverrides,
      });
    }),
  ),

  on(setStyleSheetForAction, (state, { elementId, actionId, styleSheetId, styleSheetName }) =>
    produce(state, (draft) => {
      setStyleSheetForActionMutably({
        entities: draft.entities,
        elementId,
        actionId,
        styleSheetId,
        styleSheetName,
      });
    }),
  ),

  // This reducer copies props to elements that are loaded in current view and were using deleted style.
  on(copyInactiveStyleSheetProps, (state, { elementIds, props }) =>
    produce(state, (draft) => {
      elementIds.forEach((elementId) => {
        const element = draft.entities[elementId];
        copyPropsFromInactiveStyleSheetMutably(element, props);
      });
    }),
  ),

  on(initializeAvatarFields, (state, { elementId, schema }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (isElementType(element, 'avatar')) {
        initializeAvatarFieldsMutably(element, schema);
      }
    }),
  ),

  on(setElementDescription, (state, { elementId, description }) =>
    produce(state, (draft) => {
      const element = draft.entities[elementId];
      if (element) {
        element.description = description;
      }
    }),
  ),
];

/**
 * Only the root element that is a page root can be selected. Root that are used in partial views
 * should not be selectable.
 */
function isForeignRoot(state: EngineStateBase, element?: CanvasElement): boolean {
  return isElementType(element, 'root') && !isPageRootId(element.id, state.pageId);
}

// TODO: test
function selectPathNode(
  state: EngineStateBase,
  id?: CanvasElementId,
  slot?: SlotId,
  rowContext?: RenderElementDataRowContext,
): EngineStateBase {
  if (id !== undefined) {
    const selectedElement = state.entities[id];
    // If selected element is the wrapper - always select the element and ignore the slot
    if (isWrapper(selectedElement) && selectedElement.props.wrapper.mainElementId) {
      return produce(state, (draft) => {
        draft.selectedPathNode = { id };
      });
    } else if (selectedElement?.parentNode) {
      const parentElement = state.entities[selectedElement.parentNode.id];
      // If the selected item is the main wrapped item - select the wrapper element instead
      if (isWrapper(parentElement) && parentElement.props.wrapper.mainElementId === id) {
        return produce(state, (draft) => {
          draft.selectedPathNode = { id: parentElement.id };
        });
      }
    }

    // Nav elements like nav container or sidebar should not be selectable by the user,
    // and neither are roots hosted inside them (Header/Sidebar/Bottombar page roots)
    if (isNavigationElement(selectedElement) || isForeignRoot(state, selectedElement)) {
      return state;
    }

    // Otherwise - select what the user selected
    return produce(state, (draft) => {
      draft.selectedPathNode =
        isVariantOfFormContainer(selectedElement) || isFormGroupLike(selectedElement)
          ? { id }
          : { id, slot, rowContext };
    });
  } else {
    return state;
  }
}

function selectPathNodeMutably(
  draft: Draft<EngineStateBase>,
  id?: CanvasElementId,
  slot?: SlotId,
  rowContext?: RenderElementDataRowContext,
): void {
  if (id !== undefined) {
    // If the selected item is the main wrapped item - select the wrapper element instead
    const selectedElement = draft.entities[id];

    // Nav elements like nav container or sidebar should not be selectable by the user,
    // and neither are roots hosted inside them (Header/Sidebar/Bottombar page roots)
    if (isNavigationElement(selectedElement) || isForeignRoot(draft, selectedElement)) {
      return;
    }

    if (selectedElement?.parentNode) {
      const parentElement = draft.entities[selectedElement.parentNode.id];
      if (isWrapper(parentElement) && parentElement.props.wrapper.mainElementId === id) {
        draft.selectedPathNode = { id: parentElement.id };
        return;
      }
    }

    // Otherwise - select what the user selected
    draft.selectedPathNode = isVariantOfFormContainer(selectedElement)
      ? { id }
      : { id, slot, rowContext };
  }
}

/**
 * Selects parent element following paths:
 * Button 1 -> Container 1 - slot 0 -> Container 1 - slot undefined -> Page Root -> end;
 *
 *                                                                        Skip root
 *                                                                      |----------|
 *                                                                      |         \/
 * Button 1 -> Container 1 - slot 0 -> Container 1 - slot undefined -> Root -> Nav Container -> Page Root -> end;
 *
 * For elements that has only one slot it tries to select the main element.
 * @param state
 * @param id
 */
function selectParentNode(state: EngineStateBase, id?: CanvasElementId): EngineStateBase {
  if (!id) {
    return state;
  }

  const element = state.entities[id];

  if (!element) {
    throw new Error(`Could not find element id ${id}`);
  }

  let parentId: CanvasElementId | undefined;
  let parentSlot: SlotId | undefined;
  const requestedParent = element.parentNode;

  /**
   * If element has selected slot before selecting its parent try to select it without slot.
   */
  const selectedPath = state.selectedPathNode;
  if (element.slots?.ids.length && selectedPath?.slot) {
    parentId = element.id;
    parentSlot = undefined;
  } else if (requestedParent) {
    parentId = requestedParent.id;
    parentSlot = requestedParent.slot;
  }

  let rowContext: RenderElementDataRowContext | undefined = undefined;
  if (parentId) {
    const resultParent = state.entities[parentId];

    /**
     * If element has only one slot select it without slot.
     */
    if (resultParent?.slots?.ids.length === 1) {
      parentSlot = undefined;
    }

    if (isElementType(resultParent, 'dialog') || isElementType(resultParent, 'drawer')) {
      // Always select the whole dialog or drawer
      parentSlot = undefined;
    } else if (isRoot(resultParent)) {
      /**
       * Root elements can be nested in another elements.
       * For example Nav Container has Root element inside it.
       * This Root element has no direct reference to its parent
       */
      const hostingElementData = findHostingOfRoot(state, resultParent);

      if (hostingElementData) {
        const hostingElement = state.entities[hostingElementData.elementId];

        parentId = hostingElementData.elementId;
        // For Nav Container go directly to the element (ignore the slot) for better UX
        parentSlot =
          hostingElement?.type !== 'navContainer' ? hostingElementData.slotId : undefined;
      }
    } else if (
      isFormList(resultParent) ||
      findParentByElementType(state.entities, id, 'editableList')
    ) {
      rowContext = selectedPath?.rowContext;
    }
  }

  return selectPathNode(state, parentId, parentSlot, rowContext);
}

function upsertLayout(
  state: EngineStateBase,
  layout: LayoutData,
  elementsToAdd: CanvasElement[] = [],
): EngineStateBase {
  let newState = entityAdapter.removeMany((element) => element.ns === layout.pageId, state);
  newState = entityAdapter.addMany(elementsToAdd, newState);

  return produce(newState, (draft) => {
    if (!draft.layoutIds?.includes(layout.pageId)) {
      draft.layoutIds?.push(layout.pageId);
    }
    draft.layoutEntities = {
      ...draft.layoutEntities,
      [layout.pageId]: layout,
    };
    if (draft.offSyncLayouts) {
      delete draft.offSyncLayouts[layout.pageId];
    }
  });
}
